/**
 * 숫자를 K, M 단위로 변환하는 함수
 * @param num 변환할 숫자
 * @returns 변환된 문자열 (예: 1500 -> "1.5K", 1500000 -> "1.5M")
 */
export const formatNumber = (num: number): string => {
  if (num < 1000) {
    return (num.toFixed(0)).toString();
  } else if (num < 1000000) {
    return (num / 1000).toFixed(0).replace(/\.0$/, '') + 'K';
  } else if (num < 1000000000) {
    return (num / 1000000).toFixed(0).replace(/\.0$/, '') + 'M';
  } else if (num < 1000000000000) {
    return (num / 1000000000).toFixed(0).replace(/\.0$/, '') + 'B';
  } else {
    return (num / 1000000000000).toFixed(0).replace(/\.0$/, '') + 'T';
  }
};

/**
 * 숫자를 가장 큰 단위로만 변환하는 함수
 * @param num 변환할 숫자
 * @returns 변환된 문자열 (예: 1500 -> "1.5K", 1500000 -> "1.5M", 2500000000 -> "2.5B")
 */
export const formatNumberToLargestUnit = (num: number): string => {
  // undefined, null, NaN 체크
  if (num === undefined || num === null || isNaN(num)) {
    return '0';
  }
  
  if (num < 1000) {
    return (num.toFixed(0)).toString();
  } else if (num < 1000000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  } else if (num < 1000000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num < 1000000000000) {
    return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  } else {
    return (num / 1000000000000).toFixed(1).replace(/\.0$/, '') + 'T';
  }
};

/**
 * 경과 시간을 가장 큰 단위로만 포맷팅하는 함수
 * @param startTime 시작 시간 (timestamp 또는 Date)
 * @param endTime 종료 시간 (timestamp 또는 Date, 기본값: 현재 시간)
 * @returns 포맷된 경과 시간 문자열 (예: "2d", "3h", "45m", "30s")
 */
export const formatElapsedTime = (startTime: number | Date | string, endTime: number | Date = Date.now()): string => {
  // 시작 시간을 밀리초로 변환
  let startMs: number;
  if (typeof startTime === 'string') {
    startMs = new Date(startTime).getTime();
  } else if (startTime instanceof Date) {
    startMs = startTime.getTime();
  } else {
    startMs = startTime;
  }

  // 종료 시간을 밀리초로 변환
  let endMs: number;
  if (endTime instanceof Date) {
    endMs = endTime.getTime();
  } else {
    endMs = endTime;
  }

  // 경과 시간 계산 (밀리초)
  const elapsedMs = endMs - startMs;
  
  // 음수인 경우 (미래 시간) 처리
  if (elapsedMs < 0) {
    return '0s';
  }

  // 초, 분, 시간, 일로 변환
  const seconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // 가장 큰 단위만 표시
  if (days > 0) {
    return `${days}d`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return `${seconds}s`;
  }
};

/**
 * 분 단위 시간을 가장 큰 단위로만 포맷팅하는 함수
 * @param minutesIn 분 단위 시간
 * @returns 포맷된 시간 문자열 (예: "2d", "3h", "45m")
 */
export const formatMinutesToLargestUnit = (minutesIn: number): string => {
  if (minutesIn === undefined || minutesIn === null || isNaN(minutesIn)) {
    return '0m';
  }

  const minutes = Math.round(minutesIn);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // 가장 큰 단위만 표시
  if (days > 0) {
    return `${days}d`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}; 